import { useEffect } from 'react';

/**
 * Lock page scroll while `isOpen` is true.
 *
 * Mobile (< 640px): we target the real scroll container (the app's `<main>`,
 * which carries `flex-1 overflow-auto` in `Layout.tsx`, NOT <body>). On iOS
 * Safari this is required for two reasons:
 *   1. Freezing <body> does nothing when the scroll lives on a child element.
 *   2. Position-fixed-on-body tricks neutralise iOS' stacking-context bugs
 *      where sticky/fixed footers leak in front of `position: fixed` modals.
 *      Locking the actual scroll container achieves the same isolation.
 * We snapshot the container's scrollTop, scroll it to the top so the modal
 * (anchored top via `items-start`) is fully visible, then freeze via
 * `overflow: hidden` + `touch-action: none`. On close we restore the previous
 * scroll position.
 *
 * Desktop (>= 640px): plain `overflow: hidden` on <body> — avoids the visible
 * jump-to-top side effect that would occur on a long page.
 */
export function useBodyScrollLock(isOpen: boolean): void {
  useEffect(() => {
    if (!isOpen) return;
    const isMobile = window.matchMedia('(max-width: 639px)').matches;

    if (isMobile) {
      const container =
        (document.querySelector('main') as HTMLElement | null) ||
        (document.scrollingElement as HTMLElement | null) ||
        document.documentElement;
      const savedScrollTop = container.scrollTop;
      const prevOverflow = container.style.overflow;
      const prevTouchAction = container.style.touchAction;
      const prevScrollBehavior = container.style.scrollBehavior;

      // Jump (not smooth) to the top so the modal sits in view, then freeze.
      container.style.scrollBehavior = 'auto';
      container.scrollTop = 0;
      container.style.overflow = 'hidden';
      container.style.touchAction = 'none';

      return () => {
        container.style.overflow = prevOverflow;
        container.style.touchAction = prevTouchAction;
        container.scrollTop = savedScrollTop;
        container.style.scrollBehavior = prevScrollBehavior;
      };
    }

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
}
